"use client";
import { useCallback } from "react"

// --- Icons ---
import { CodeBlockIcon } from "@/tiptap/icons/code-block-icon"
import { HeadingOneIcon } from "@/tiptap/icons/heading-one-icon"
import { HeadingTwoIcon } from "@/tiptap/icons/heading-two-icon"
import { HeadingThreeIcon } from "@/tiptap/icons/heading-three-icon"
import { HeadingFourIcon } from "@/tiptap/icons/heading-four-icon"
import { ImageIcon } from "@/tiptap/icons/image-icon"
import { ListIcon } from "@/tiptap/icons/list-icon"
import { ListOrderedIcon } from "@/tiptap/icons/list-ordered-icon"
import { BlockquoteIcon } from "@/tiptap/icons/blockquote-icon"
import { ListTodoIcon } from "@/tiptap/icons/list-todo-icon"
import { AiSparklesIcon } from "@/tiptap/icons/ai-sparkles-icon"
import { MinusIcon } from "@/tiptap/icons/minus-icon"
import { TypeIcon } from "@/tiptap/icons/type-icon"
import { AtSignIcon } from "@/tiptap/icons/at-sign-icon"
import { SmilePlusIcon } from "@/tiptap/icons/smile-plus-icon"
import { TableIcon } from "@/tiptap/icons/table-icon"
import { ExternalLinkIcon } from "@/tiptap/icons/external-link-icon"
import { AccordionIcon } from "@/tiptap/icons/accordion-icon"
import { CalloutIcon } from "@/tiptap/icons/callout-icon"
import { buildCalloutVariantActions } from "@/tiptap/ui/callout-menu/callout-menu"

// --- Lib ---
import {
  isExtensionAvailable,
  isNodeInSchema,
  sanitizeUrl,
} from "@/tiptap/lib/tiptap-utils"
import { normalizeFirstPartyFileSrc } from "@/tiptap/core/editor-clipboard"
import {
  findSelectionPosition,
  hasContentAbove,
} from "@/tiptap/lib/tiptap-advanced-utils"

import { addEmojiTrigger } from "@/tiptap/ui/emoji-trigger-button"
import { addMentionTrigger } from "@/tiptap/ui/mention-trigger-button"
import {
  DEFAULT_CODE_BLOCK_LANGUAGE,
  setCodeBlockPreservingList,
} from "@/tiptap/ui/code-block-button/use-code-block"
import { toggleList } from "@/tiptap/ui/list-button/use-list"

const texts = {
  // AI
  continue_writing: {
    title: "Continue Writing",
    subtext: "Continue writing from the current position",
    keywords: ["continue", "write", "continue writing", "ai"],
    badge: AiSparklesIcon,
    group: "AI",
  },
  ai_ask_button: {
    title: "Ask AI",
    subtext: "Ask AI to generate content",
    keywords: ["ai", "ask", "generate"],
    badge: AiSparklesIcon,
    group: "AI",
  },

  // Style
  text: {
    title: "Text",
    subtext: "Regular text paragraph",
    keywords: ["p", "paragraph", "text"],
    badge: TypeIcon,
    group: "Style",
  },
  heading_1: {
    title: "Heading 1",
    subtext: "Top-level heading",
    keywords: ["h", "heading1", "h1"],
    badge: HeadingOneIcon,
    group: "Style",
  },
  heading_2: {
    title: "Heading 2",
    subtext: "Key section heading",
    keywords: ["h2", "heading2", "subheading"],
    badge: HeadingTwoIcon,
    group: "Style",
  },
  heading_3: {
    title: "Heading 3",
    subtext: "Subsection and group heading",
    keywords: ["h3", "heading3", "subheading"],
    badge: HeadingThreeIcon,
    group: "Style",
  },
  heading_4: {
    title: "Heading 4",
    subtext: "Minor section heading",
    keywords: ["h4", "heading4", "subheading"],
    badge: HeadingFourIcon,
    group: "Style",
  },
  bullet_list: {
    title: "Bullet List",
    subtext: "List with unordered items",
    keywords: ["ul", "li", "list", "bulletlist", "bullet list"],
    badge: ListIcon,
    group: "Style",
  },
  ordered_list: {
    title: "Numbered List",
    subtext: "List with ordered items",
    keywords: ["ol", "li", "list", "numberedlist", "numbered list"],
    badge: ListOrderedIcon,
    group: "Style",
  },
  task_list: {
    title: "To-do list",
    subtext: "List with tasks",
    keywords: ["tasklist", "task list", "todo", "checklist"],
    badge: ListTodoIcon,
    group: "Style",
  },
  quote: {
    title: "Blockquote",
    subtext: "Blockquote block",
    keywords: ["quote", "blockquote"],
    badge: BlockquoteIcon,
    group: "Style",
  },
  code_block: {
    title: "Code Block",
    subtext: "Code block with syntax highlighting",
    keywords: ["code", "pre"],
    badge: CodeBlockIcon,
    group: "Style",
  },
  callout: {
    title: "Callout",
    subtext: "Highlighted informational block",
    keywords: ["callout", "info", "success", "warning", "danger", "note", "alert"],
    badge: CalloutIcon,
    group: "Style",
  },

  // Insert
  mention: {
    title: "Mention",
    subtext: "Mention a user or item",
    keywords: ["mention", "user", "item", "tag"],
    badge: AtSignIcon,
    group: "Insert",
  },
  emoji: {
    title: "Emoji",
    subtext: "Insert an emoji",
    keywords: ["emoji", "emoticon", "smiley"],
    badge: SmilePlusIcon,
    group: "Insert",
  },
  table: {
    title: "Table",
    subtext: "Insert a table",
    aliases: ["table", "insertTable"],
    badge: TableIcon,
    group: "Insert",
  },
  divider: {
    title: "Separator",
    subtext: "Horizontal line to separate content",
    keywords: ["hr", "horizontalRule", "line", "separator"],
    badge: MinusIcon,
    group: "Insert",
  },
  accordion: {
    title: "Accordion",
    subtext: "Insert a collapsible accordion block",
    keywords: ["accordion", "details", "summary", "toggle", "collapse", "collapsible"],
    badge: AccordionIcon,
    group: "Insert",
  },
  media: {
    title: "Media",
    subtext: "Embed a YouTube video or iframe URL",
    keywords: ["media", "embed", "video", "youtube", "iframe", "url"],
    badge: ExternalLinkIcon,
    group: "Insert",
    className: "tiptap-slash-item--media",
  },

  // Upload
  image: {
    title: "Image",
    subtext: "Resizable image with caption",
    keywords: [
      "image",
      "imageUpload",
      "upload",
      "img",
      "picture",
      "media",
      "url",
    ],
    badge: ImageIcon,
    group: "Upload",
  },
  inline_image: {
    title: "Inline image",
    subtext: "Small icon inside text",
    keywords: [
      "inline",
      "icon",
      "image",
      "img",
      "picture",
      "upload",
    ],
    badge: ImageIcon,
    group: "Upload",
  },
}

// Callouts only allow text, headings, and lists — hide slash items whose
// insertion would otherwise split or remove the callout.
export const CALLOUT_DISALLOWED_SLASH_ITEMS = new Set([
  "quote",
  "code_block",
  "table",
  "divider",
  "accordion",
  "media",
  "image",
  "inline_image",
])

// Accordion titles only allow a paragraph or heading. Lists/callouts/code
// already no-op via the schema; table, separator, and accordion insert by
// splitting the details node (extra accordion + block below).
export const ACCORDION_TITLE_DISALLOWED_SLASH_ITEMS = new Set([
  "bullet_list",
  "ordered_list",
  "task_list",
  "quote",
  "code_block",
  "callout",
  "table",
  "divider",
  "accordion",
  "media",
  "image",
  "inline_image",
])

export function isSlashItemDisallowedInContext(editor, itemType) {
  if (editor.isActive("callout") && CALLOUT_DISALLOWED_SLASH_ITEMS.has(itemType)) {
    return true
  }

  if (editor.isActive("detailsSummary") && ACCORDION_TITLE_DISALLOWED_SLASH_ITEMS.has(itemType)) {
    return true
  }

  return false
}

export const getItemImplementations = () => {
  return {
    // AI
    continue_writing: {
      check: (editor) => {
        const { hasContent } = hasContentAbove(editor)
        const extensionsReady = isExtensionAvailable(editor, [
          "ai",
          "aiAdvanced",
        ])
        return extensionsReady && hasContent
      },
      action: ({
        editor
      }) => {
        const editorChain = editor.chain().focus()

        const nodeSelectionPosition = findSelectionPosition({ editor })

        if (nodeSelectionPosition !== null) {
          editorChain.setNodeSelection(nodeSelectionPosition)
        }

        editorChain.run()

        editor.chain().focus().aiGenerationShow().run()

        requestAnimationFrame(() => {
          const { hasContent, content } = hasContentAbove(editor)

          const snippet =
            content.length > 500 ? `...${content.slice(-500)}` : content

          const prompt = hasContent
            ? `Context: ${snippet}\n\nContinue writing from where the text above ends. Write ONLY ONE SENTENCE. DONT REPEAT THE TEXT.`
            : "Start writing a new paragraph. Write ONLY ONE SENTENCE."

          editor
            .chain()
            .focus()
            .aiTextPrompt({
              stream: true,
              format: "rich-text",
              text: prompt,
            })
            .run()
        })
      },
    },
    ai_ask_button: {
      check: (editor) =>
        isExtensionAvailable(editor, ["ai", "aiAdvanced"]),
      action: ({
        editor
      }) => {
        const editorChain = editor.chain().focus()

        const nodeSelectionPosition = findSelectionPosition({ editor })

        if (nodeSelectionPosition !== null) {
          editorChain.setNodeSelection(nodeSelectionPosition)
        }

        editorChain.run()

        editor.chain().focus().aiGenerationShow().run()
      },
    },

    // Style
    text: {
      check: (editor) => isNodeInSchema("paragraph", editor),
      action: ({
        editor
      }) => {
        editor.chain().focus().setParagraph().run()
      },
    },
    heading_1: {
      check: (editor) => isNodeInSchema("heading", editor),
      action: ({
        editor
      }) => {
        editor.chain().focus().toggleHeading({ level: 1 }).run()
      },
    },
    heading_2: {
      check: (editor) => isNodeInSchema("heading", editor),
      action: ({
        editor
      }) => {
        editor.chain().focus().toggleHeading({ level: 2 }).run()
      },
    },
    heading_3: {
      check: (editor) => isNodeInSchema("heading", editor),
      action: ({
        editor
      }) => {
        editor.chain().focus().toggleHeading({ level: 3 }).run()
      },
    },
    heading_4: {
      check: (editor) => isNodeInSchema("heading", editor),
      action: ({
        editor
      }) => {
        editor.chain().focus().toggleHeading({ level: 4 }).run()
      },
    },
    bullet_list: {
      check: (editor) => isNodeInSchema("bulletList", editor),
      action: ({
        editor
      }) => {
        toggleList(editor, "bulletList")
      },
    },
    ordered_list: {
      check: (editor) => isNodeInSchema("orderedList", editor),
      action: ({
        editor
      }) => {
        toggleList(editor, "orderedList")
      },
    },
    task_list: {
      check: (editor) => isNodeInSchema("taskList", editor),
      action: ({
        editor
      }) => {
        toggleList(editor, "taskList")
      },
    },
    quote: {
      check: (editor) => isNodeInSchema("blockquote", editor),
      action: ({
        editor
      }) => {
        editor.chain().focus().toggleBlockquote().run()
      },
    },
    code_block: {
      check: (editor) => isNodeInSchema("codeBlock", editor),
      action: ({
        editor
      }) => {
        setCodeBlockPreservingList(editor, {
          language: DEFAULT_CODE_BLOCK_LANGUAGE,
        })
      },
    },
    callout: {
      check: (editor) => isNodeInSchema("callout", editor),
      action: ({
        editor
      }) => {
        editor.chain().focus().setCallout({ variant: "info" }).run()
      },
    },

    // Insert
    mention: {
      check: (editor) =>
        isExtensionAvailable(editor, ["mention", "mentionAdvanced"]),
      action: ({
        editor
      }) => addMentionTrigger(editor),
    },
    emoji: {
      check: (editor) =>
        isExtensionAvailable(editor, ["emoji", "emojiPicker"]),
      action: ({
        editor
      }) => addEmojiTrigger(editor),
    },
    divider: {
      check: (editor) => isNodeInSchema("horizontalRule", editor),
      action: ({
        editor
      }) => {
        if (editor.isActive("detailsSummary")) return
        editor.chain().focus().setHorizontalRule().run()
      },
    },
    table: {
      check: (editor) => isNodeInSchema("table", editor),
      action: ({
        editor
      }) => {
        if (editor.isActive("detailsSummary")) return
        editor
          .chain()
          .focus()
          .insertTable({
            rows: 3,
            cols: 3,
            withHeaderRow: false,
          })
          .run()
      },
    },
    accordion: {
      check: (editor) => isNodeInSchema("details", editor),
      action: ({
        editor
      }) => {
        if (editor.isActive("detailsSummary")) return
        editor
          .chain()
          .focus()
          .insertContent({
            type: "details",
            content: [
              {
                type: "detailsSummary",
                content: [
                  {
                    type: "paragraph",
                    content: [{ type: "text", text: "Accordion title" }],
                  },
                ],
              },
              {
                type: "detailsContent",
                content: [
                  {
                    type: "paragraph",
                    content: [{ type: "text", text: "Accordion content" }],
                  },
                ],
              },
            ],
          })
          .run()
      },
    },
    media: {
      check: (editor) =>
        isNodeInSchema("youtube", editor) || isNodeInSchema("iframe", editor),
      action: () => {},
    },

    // Upload
    image: {
      check: (editor) => isNodeInSchema("image", editor),
      action: ({
        editor
      }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: "imageUpload",
          })
          .run()
      },
    },
    inline_image: {
      check: (editor) => isNodeInSchema("inlineImage", editor),
      action: ({
        editor
      }) => pickAndUploadInlineImage(editor),
    },
  };
}

function getImageUploadExtension(editor) {
  return editor.extensionManager.extensions.find((extension) => extension.name === "imageUpload")
}

export function insertInlineImage(editor, attrs, position) {
  if (!editor?.isEditable) return false

  const content = {
    type: "inlineImage",
    attrs,
  }
  const chain = editor.chain().focus()
  if (Number.isInteger(position)) {
    const maxPos = editor.state.doc.content.size
    return chain.insertContentAt(Math.min(Math.max(1, position), maxPos), content).run()
  }

  return chain.insertContent(content).run()
}

export function pickAndUploadInlineImage(editor) {
  if (!editor?.isEditable) return

  const uploadExtension = getImageUploadExtension(editor)
  const upload = uploadExtension?.options?.upload
  if (!upload) return

  const input = document.createElement("input")
  input.type = "file"
  input.accept = uploadExtension.options.accept || "image/*"
  input.hidden = true
  input.addEventListener("change", async () => {
    const file = input.files?.[0]
    input.remove()
    if (!file) return

    const insertPos = editor.state.selection.from

    try {
      const src = await upload(file)
      const name = file.name.replace(/\.[^/.]+$/, "") || "image"
      insertInlineImage(editor, { src, alt: name, title: name }, insertPos)
    } catch (error) {
      uploadExtension.options.onError?.(error)
    }
  })
  document.body.appendChild(input)
  input.click()
}

export function organizeItemsByGroups(items, showGroups) {
  if (!showGroups) {
    return items.map((item) => ({ ...item, group: "" }));
  }

  const groups = {}

  // Group items
  items.forEach((item) => {
    const groupLabel = item.group || ""
    if (!groups[groupLabel]) {
      groups[groupLabel] = []
    }
    groups[groupLabel].push(item)
  })

  // Flatten groups in order (this maintains the visual order for keyboard navigation)
  const organizedItems = []
  Object.entries(groups).forEach(([, groupItems]) => {
    organizedItems.push(...groupItems)
  })

  return organizedItems
}

export function filterSlashMenuItems(items, query, showGroups) {
  const normalizedQuery = query.trim().toLowerCase()
  const filteredItems = normalizedQuery
    ? items.filter((item) => item.title.toLowerCase().includes(normalizedQuery))
    : items

  return organizeItemsByGroups(filteredItems, showGroups)
}

function attachParentItem(subItem, parentItem) {
  return {
    ...subItem,
    parentItem,
  }
}

export function getSlashMenuNavigationItems(items, query = "") {
  const normalizedQuery = query.trim().toLowerCase()

  if (normalizedQuery && items.length === 1 && items[0]?.subItems?.length) {
    return items[0].subItems.map((subItem) => attachParentItem(subItem, items[0]))
  }

  return items.flatMap((item) => {
    if (!item?.subItems?.length) {
      return [item]
    }

    return [
      item,
      ...item.subItems.map((subItem) => attachParentItem(subItem, item)),
    ]
  })
}

function buildCalloutSubItems(editor) {
  return buildCalloutVariantActions(editor).map((item) => ({
    calloutVariant: item.calloutVariant,
    title: item.label,
    subtext: `${item.label} callout block`,
    keywords: ["callout", item.variant],
    badge: item.icon,
    onSelect: item.onSelect,
  }))
}

function isYouTubeUrl(url) {
  try {
    const parsedUrl = new URL(url)
    const hostname = parsedUrl.hostname.toLowerCase().replace(/^www\./, "")

    return (
      hostname === "youtu.be" ||
      hostname === "youtube.com" ||
      hostname.endsWith(".youtube.com") ||
      hostname === "youtube-nocookie.com" ||
      hostname.endsWith(".youtube-nocookie.com")
    )
  } catch {
    return false
  }
}

function normalizeMediaDimension(value, fallback) {
  const trimmedValue = value?.toString().trim()

  if (!trimmedValue) {
    return fallback
  }

  return /^\d+$/.test(trimmedValue) ? trimmedValue : fallback
}

export const MEDIA_NODE_TYPES = ["youtube", "video", "audio", "iframe"]

export function isMediaNodeType(typeName) {
  return MEDIA_NODE_TYPES.includes(typeName)
}

const UNSAFE_MEDIA_SCHEME = /^(javascript|blob|data|vbscript):/i
const HAS_MEDIA_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i
const AUDIO_EXTENSION = /\.(mp3|wav|ogg|m4a|aac|flac)$/
const VIDEO_EXTENSION = /\.(mp4|webm|ogv|mov|m4v)$/
const LOCAL_UPLOAD_PATH = /^\/(?:file|shared_file)\/[^/]+$|^\/portal\/[^/]+\/file\/[^/]+$/

export function resolveMediaSource(rawUrl) {
  const trimmedUrl = rawUrl?.trim()

  if (!trimmedUrl) {
    return null
  }

  if (UNSAFE_MEDIA_SCHEME.test(trimmedUrl)) {
    return null
  }

  // Reject protocol-relative URLs and backslash host tricks (//host, /\host).
  if (/^[/\\]{2}/.test(trimmedUrl) || trimmedUrl.includes("\\")) {
    return null
  }

  // Same-origin relative paths must stay relative. Prepending https:// turns
  // `/file/{slug}` into `https://file/{slug}` (file becomes the hostname).
  if (trimmedUrl.startsWith("/")) {
    return trimmedUrl
  }

  const normalizedInput = HAS_MEDIA_SCHEME.test(trimmedUrl)
    ? trimmedUrl
    : `https://${trimmedUrl}`

  const normalizedUrl = sanitizeUrl(
    normalizedInput,
    typeof window !== "undefined" ? window.location.href : undefined,
    ["http", "https"]
  )

  if (!normalizedUrl) {
    return null
  }

  if (normalizedUrl === "#") {
    return null
  }

  return normalizeFirstPartyFileSrc(normalizedUrl)
}

function mediaPathname(url) {
  try {
    return new URL(url, "https://hudu.invalid").pathname.toLowerCase()
  } catch {
    return url.split(/[?#]/)[0].toLowerCase()
  }
}

function detectHtml5MediaType(url) {
  const pathname = mediaPathname(url)

  if (AUDIO_EXTENSION.test(pathname)) {
    return "audio"
  }

  if (VIDEO_EXTENSION.test(pathname) || LOCAL_UPLOAD_PATH.test(pathname)) {
    return "video"
  }

  return null
}

export function buildMediaNode(editor, url, options = {}) {
  const width = normalizeMediaDimension(options.width, "640")
  const height = normalizeMediaDimension(options.height, "480")

  if (isYouTubeUrl(url) && isNodeInSchema("youtube", editor)) {
    return {
      type: "youtube",
      attrs: {
        src: url,
        width,
        height,
      },
    }
  }

  const mediaType = detectHtml5MediaType(url)

  if (mediaType === "audio" && isNodeInSchema("audio", editor)) {
    return {
      type: "audio",
      attrs: {
        src: url,
        controls: "controls",
      },
    }
  }

  if (mediaType === "video" && isNodeInSchema("video", editor)) {
    return {
      type: "video",
      attrs: {
        src: url,
        controls: "controls",
        width,
        height,
      },
    }
  }

  if (isNodeInSchema("iframe", editor)) {
    return {
      type: "iframe",
      attrs: {
        src: url,
        width,
        height,
        frameborder: "0",
        allowfullscreen: "true",
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
        title: "Embedded media",
      },
    }
  }

  return null
}

function attributesFromElement(element, attributeNames) {
  return attributeNames.reduce((attributes, name) => {
    const value = element.getAttribute(name)
    if (value !== null && value !== "") {
      attributes[name] = value
    }
    return attributes
  }, {})
}

function serializeAttributes(attributes) {
  return Object.entries(attributes)
    .filter(([, value]) => value !== null && typeof value !== "undefined" && value !== "")
    .map(([name, value]) => `${name}="${String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;")}"`)
    .join(" ")
}

function mediaNodeToEmbedHtml(node) {
  if (!node || !isMediaNodeType(node.type?.name)) return ""

  const attrs = node.attrs || {}
  const attributeHtml = serializeAttributes(attrs)

  switch (node.type.name) {
    case "iframe":
      return `<iframe ${attributeHtml}></iframe>`
    case "video":
      return `<video ${attributeHtml}></video>`
    case "audio":
      return `<audio ${attributeHtml}></audio>`
    default:
      return ""
  }
}

export function buildMediaNodeFromEmbedHtml(editor, rawHtml) {
  if (!editor || typeof document === "undefined") return null

  const container = document.createElement("div")
  container.innerHTML = rawHtml?.trim() || ""

  const iframe = container.querySelector("iframe")
  if (iframe && isNodeInSchema("iframe", editor)) {
    const src = resolveMediaSource(iframe.getAttribute("src"))
    if (!src) return null

    return {
      type: "iframe",
      attrs: {
        ...attributesFromElement(iframe, [
          "width",
          "height",
          "frameborder",
          "allow",
          "title",
          "style",
          "class",
        ]),
        src,
        ...(iframe.hasAttribute("allowfullscreen") ? { allowfullscreen: "true" } : {}),
      },
    }
  }

  const video = container.querySelector("video")
  if (video && isNodeInSchema("video", editor)) {
    const source = video.getAttribute("src") || video.querySelector("source")?.getAttribute("src")
    const src = resolveMediaSource(source)
    if (!src) return null

    return {
      type: "video",
      attrs: {
        ...attributesFromElement(video, [
          "poster",
          "controls",
          "autoplay",
          "loop",
          "muted",
          "preload",
          "width",
          "height",
          "style",
          "class",
        ]),
        src,
        controls: video.hasAttribute("controls") ? "controls" : "controls",
      },
    }
  }

  const audio = container.querySelector("audio")
  if (audio && isNodeInSchema("audio", editor)) {
    const source = audio.getAttribute("src") || audio.querySelector("source")?.getAttribute("src")
    const src = resolveMediaSource(source)
    if (!src) return null

    return {
      type: "audio",
      attrs: {
        ...attributesFromElement(audio, [
          "controls",
          "autoplay",
          "loop",
          "muted",
          "preload",
          "style",
          "class",
        ]),
        src,
        controls: audio.hasAttribute("controls") ? "controls" : "controls",
      },
    }
  }

  const oembed = container.querySelector("oembed")
  const oembedUrl = oembed?.getAttribute("url")
  if (oembedUrl) {
    const url = resolveMediaSource(oembedUrl)
    return url ? buildMediaNode(editor, url) : null
  }

  return null
}

export function getMediaFormValues(node) {
  if (!node || !isMediaNodeType(node.type?.name)) {
    return null
  }

  return {
    source: node.attrs.src || "",
    width: node.attrs.width?.toString() || "640",
    height: node.attrs.height?.toString() || "480",
    embedHtml: mediaNodeToEmbedHtml(node),
  }
}

export function insertMedia(editor, options) {
  if (!editor) return false

  if (options?.mode === "embed" || options?.embedHtml) {
    const mediaNode = buildMediaNodeFromEmbedHtml(editor, options?.embedHtml)

    if (!mediaNode) {
      return false
    }

    return editor.chain().focus().insertContent(mediaNode).run()
  }

  const source = typeof options === "string" ? options : options?.source
  const normalizedUrl = resolveMediaSource(source)

  if (!normalizedUrl) {
    return false
  }

  const mediaNode = buildMediaNode(editor, normalizedUrl, options)

  if (!mediaNode) {
    return false
  }

  return editor.chain().focus().insertContent(mediaNode).run()
}

export function updateMediaNode(editor, nodePos, node, options) {
  if (!editor || !node || !isMediaNodeType(node.type?.name)) {
    return false
  }

  if (options?.mode === "embed" || options?.embedHtml) {
    const mediaNode = buildMediaNodeFromEmbedHtml(editor, options?.embedHtml)

    if (!mediaNode) {
      return false
    }

    return editor
      .chain()
      .focus()
      .insertContentAt({ from: nodePos, to: nodePos + node.nodeSize }, mediaNode)
      .run()
  }

  const normalizedUrl = resolveMediaSource(options?.source)

  if (!normalizedUrl) {
    return false
  }

  const mediaNode = buildMediaNode(editor, normalizedUrl, options)

  if (!mediaNode) {
    return false
  }

  return editor
    .chain()
    .focus()
    .insertContentAt({ from: nodePos, to: nodePos + node.nodeSize }, mediaNode)
    .run()
}

export function createMediaInsertItem(options) {
  return {
    title: "Media",
    onSelect: ({ editor }) => insertMedia(editor, options),
  }
}

/**
 * Custom hook for slash dropdown menu functionality
 */
export function useSlashDropdownMenu(config) {
  const getSlashMenuItems = useCallback((editor) => {
    const items = []

    const enabledItems =
      config?.enabledItems || (Object.keys(texts))
    const showGroups = config?.showGroups !== false

    const itemImplementations = getItemImplementations()

    enabledItems.forEach((itemType) => {
      if (isSlashItemDisallowedInContext(editor, itemType)) return

      const itemImpl = itemImplementations[itemType]
      const itemText = texts[itemType]

      if (itemImpl && itemText && itemImpl.check(editor)) {
        const item = {
          onSelect: ({ editor }) => itemImpl.action({ editor }),
          ...itemText,
        }

        if (itemType === "callout") {
          item.subItems = buildCalloutSubItems(editor)
        }

        if (itemType === "media") {
          item.panel = "media"
        }

        if (config?.itemGroups?.[itemType]) {
          item.group = config.itemGroups[itemType]
        } else if (!showGroups) {
          item.group = ""
        }

        items.push(item)
      }
    })

    if (config?.customItems) {
      items.push(...config.customItems)
    }

    // Reorganize items by groups to ensure keyboard navigation works correctly
    return organizeItemsByGroups(items, showGroups);
  }, [config])

  return {
    getSlashMenuItems,
    config,
  }
}
